import { isDefined } from "@clipboard-health/util-ts";
import { type Theme } from "@mui/material";
import {
  CardWithIntent,
  type CardWithIntentProps,
  mergeSxProps,
} from "@src/appV2/redesign/system/ui-components";
import type { IntentVariant } from "@src/appV2/redesign/system/ui-theme";
import type { Ref } from "react";
import { forwardRef } from "react";

export type ShiftBottomSheetInnerCardIntent = IntentVariant | "priority" | "limited";

interface ShiftBottomSheetInnerCardProps extends Omit<CardWithIntentProps, "intent"> {
  intent?: ShiftBottomSheetInnerCardIntent;
}

function borderColor(intent: ShiftBottomSheetInnerCardIntent, theme: Theme) {
  if (intent === "priority") {
    return theme.palette.shift?.priority.border;
  }

  if (intent === "limited") {
    // This was using the tiers palette which is no longer in our theme
    // -> "theme.palette.tiers?.limited.light"
    // Please either switch to another color, or add this one to the theme under a different palette
    // eslint-disable-next-line no-restricted-syntax
    return "#F7D0D0";
  }

  return undefined;
}

function intentOverride(intent: ShiftBottomSheetInnerCardIntent): IntentVariant | undefined {
  return intent === "priority" || intent === "limited" ? undefined : intent;
}

function BaseShiftBottomSheetInnerCard(
  props: ShiftBottomSheetInnerCardProps,
  ref: Ref<HTMLDivElement>
) {
  const { sx, intent = "neutral", ...restProps } = props;

  const overriddenIntent = intentOverride(intent);

  return (
    <CardWithIntent
      {...restProps}
      ref={ref}
      intent={overriddenIntent}
      sx={mergeSxProps(
        (theme) => ({
          minHeight: "20rem",
          border: isDefined(overriddenIntent)
            ? undefined
            : `1px solid ${borderColor(intent, theme)}`,
        }),
        sx
      )}
    />
  );
}

export const ShiftBottomSheetInnerCard = forwardRef(BaseShiftBottomSheetInnerCard);
